"use client"

import {
  FacebookShareButton,
  TwitterShareButton,
  RedditShareButton,
  FacebookIcon,
  RedditIcon
} from 'react-share'
import { Button } from "@/components/ui/button"
import { Share2, Copy } from "lucide-react"
import { toast } from "sonner"
import { communityAnalytics } from "@/lib/analytics"

// Custom X (Twitter) Icon Component
function XIcon({ size = 40 }: { size?: number }) {
  return (
    <div
      className="inline-flex items-center justify-center rounded-full bg-black hover:opacity-80 transition-opacity cursor-pointer"
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="text-white"
        style={{ width: size * 0.5, height: size * 0.5 }}
        fill="currentColor"
      >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    </div>
  )
}

interface ShareButtonsProps {
  photoSlug: string
  photoId: string // Still needed for API calls
  title: string
  compact?: boolean
}

export function ShareButtons({ photoSlug, photoId, title, compact = false }: ShareButtonsProps) {
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/community/${photoSlug}`
    : `/community/${photoSlug}`
  const shareTitle = `Check out this amazing lawn: ${title}`
  const hashtags = ['LawnCare', 'Gardening', 'WhatGrassIsThis']

  const isNativeShareAvailable = typeof navigator !== 'undefined' && navigator.share

  const handleNativeShare = async () => {
    if (isNativeShareAvailable) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        })
        trackShare('native_share')
        toast.success('Shared successfully!')
      } catch {
        // User cancelled or share failed.
      }
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      trackShare('copy_link')
      toast.success('Link copied to clipboard!')
    } catch (error) {
      console.error('Failed to copy link:', error)
      toast.error('Failed to copy link')
    }
  }

  const trackShare = async (platform: string) => {
    communityAnalytics.shareCompleted(photoId, platform)
    try {
      await fetch(`/api/community/photos/${photoId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform })
      })
    } catch (error) {
      console.error('Failed to track share:', error)
    }
  }

  const iconSize = compact ? 32 : 40

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        {/* Native share or copy link for compact mode */}
        {isNativeShareAvailable ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNativeShare}
            aria-label={`Share ${title}`}
            className="p-1 h-11 w-11"
          >
            <Share2 className="w-4 h-4" aria-hidden="true" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyLink}
            aria-label={`Copy link to ${title}`}
            className="p-1 h-11 w-11"
          >
            <Copy className="w-4 h-4" aria-hidden="true" />
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
      <span className="text-sm text-gray-600 font-medium">Share:</span>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Popup providers cannot confirm that a post was completed. Only
            native-share success and a successful clipboard write count. */}
        <TwitterShareButton
          aria-label={`Share ${title} on X`}
          url={shareUrl}
          title={shareTitle}
          hashtags={hashtags}
          onClick={() => communityAnalytics.shareOpened(photoId, 'twitter')}
        >
          <XIcon size={iconSize} />
        </TwitterShareButton>

        <FacebookShareButton
          aria-label={`Share ${title} on Facebook`}
          url={shareUrl}
          hashtag="#LawnCare"
          onClick={() => communityAnalytics.shareOpened(photoId, 'facebook')}
        >
          <FacebookIcon size={iconSize} round />
        </FacebookShareButton>

        <RedditShareButton
          aria-label={`Share ${title} on Reddit`}
          url={shareUrl}
          title={shareTitle}
          onClick={() => communityAnalytics.shareOpened(photoId, 'reddit')}
        >
          <RedditIcon size={iconSize} round />
        </RedditShareButton>

        {/* Copy link button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
          aria-label={`Copy link to ${title}`}
          className="flex-shrink-0"
        >
          <Copy className="w-4 h-4 sm:mr-1" aria-hidden="true" />
          <span className="hidden sm:inline ml-1">Copy Link</span>
        </Button>
      </div>
    </div>
  )
}
